// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AddressDetails from './../../components/AddressDetails';
import DeviceInformation from './../../components/DeviceInformation/DeviceInformation';
import ClaimOrder from './../../components/ClaimOrder';
import CommonLayout from './../../components/CommonLayout';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import history from 'src/history';
import {
    IClaimOrder,
} from './../../businessLogic/sagas/serviceRequest/types';
import { LoadingState } from './../../store/actionStatus';
import {
    claimsSelector,
    deviceSelector
} from './../../store/deviceInfo/selectors';

import {
    PURCHASE_ORDER
} from './../../store/orderCreation';

import Styles from './orderDetail.module.scss';

function Address({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();

    return (
        <div className={Styles.address}>
            <h2 className={Styles.detailSection}>{t('address_details')}</h2>
            <AddressDetail claimOrder={claimOrder} />
        </div>
    );
}

function AddressDetail({ claimOrder }: { claimOrder: IClaimOrder }) {
    return (
        <AddressDetails data={claimOrder} />
    );
}

function Details({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();
    return (
        <div>
            <h2 className={Styles.detailSection}>{t('order_details')}</h2>
            <ClaimOrder data={claimOrder} />
        </div>
    );
}

function isServiceRequestResponseParsed(data: any): data is IClaimOrder {
    return data && 'claimOrderId' in data
        && Array.isArray(data.serviceRequests);
}

export default function OrderConfirmation() {
    const { t } = useTranslation();

    const orderStatus = useSelector(state => statusSelector(state)(PURCHASE_ORDER));

    //TODO: Error case must be handled
    return (
        <CommonLayout
            loading={orderStatus.status === LoadingState.started}
            loadingMessage={t('looking_up_order')}
            pageTitle={[t('order_detail')]}
        >
            <Page />
        </CommonLayout>);
}

function Page() {
    const claimOrders: IClaimOrder[] = useSelector((state: any) => state.device.claims);
    if (!claimOrders || !claimOrders.length) {
        alert('No claim order found. Redirecting back to home page.');
        history.push('/');
    }

    const claimOrder: IClaimOrder = claimOrders[0];

    if (!isServiceRequestResponseParsed(claimOrder)) {
        return <RenderErrorMessage />;
    }

    return (
        <div className={Styles.pageBody}>
            <DeviceInfo />
            <Details claimOrder={claimOrder} />
            <Address claimOrder={claimOrder} />
        </div>
    );
}

function RenderErrorMessage() {
    const { t } = useTranslation();
    return <div className={Styles.errorOccurred}>{t('error_occurred')}</div>;
}

function DeviceInfo() {
    const deviceInfo: any = useSelector((state: any) => deviceSelector(state));
    const claimOrder = useSelector((state: any) => claimsSelector(state))[0];

    const { part = { repairProduct: {} } } = deviceInfo;
    
    const props = {
        isCreateOrderView: false,
        claimOrderNumber: claimOrder.claimOrderId,
        country: part.marketplaceName,
        description: part.description,
        deviceName: part.repairProduct.name,
        image: part.repairProduct.imageUri,
        marketPlace: part.marketplaceName,
        partNumber: part.partNumber,
        resetPath: '/',
        serialNumber: deviceInfo.serialNumber,
        seriesName: part.msPressSeriesName,
        status: deviceInfo.status,
        orderType: true,
        deviceSDTRunDetail: deviceInfo.deviceSDTRunDetail,
        countryOrigin:deviceInfo.attributes?.countryOfOrigin,
        color:deviceInfo.attributes?.color,
    };
    return (
        <div className={Styles.pageType}>
            <DeviceInformation {...props} />
        </div>
    );

}
